'use client';

import { type Dispatch, type SetStateAction, useState } from 'react';
import { Popup } from '../Popup';
import { Text } from '../Text';
import { IconResolver } from '../IconResolver';
import { AnimatedIcon } from '../AnimatedIcon';
import { Anchor } from '../Anchor';
import { tv } from '../_lib/utils';
import { InlineWidget } from 'react-calendly';
import { FormBlock, type FormBlockProps } from '../FormBlock';

const multipleChoiceCtaActionStyles = tv({
	slots: {
		base: 'flex flex-col gap-3',
		option:
			'group/button relative p-5 flex gap-2 items-center border rounded-lg border-white/20 transition-colors duration-fast hover:bg-button-secondary-background-hover',
		label: 'text-start',
	},
	variants: {
		optionStyle: {
			square: {
				base: 'md:mt-6 md:grid md:gap-4',
				option: 'md:flex-col md:gap-4 md:aspect-square md:justify-center',
				label: 'md:text-center',
			},
			horizontal: {
				base: 'grow',
			},
		},
		columns: {
			0: {},
			1: {},
			2: {},
			3: {},
			4: {},
		},
	},
	compoundVariants: [
		{
			optionStyle: 'square',
			columns: 0,
			className: { base: 'md:grid-cols-1 w-1/4' },
		},
		{
			optionStyle: 'square',
			columns: 1,
			className: { base: 'md:grid-cols-1 w-1/4' },
		},
		{
			optionStyle: 'square',
			columns: 2,
			className: { base: 'md:grid-cols-2 w-1/2' },
		},
		{
			optionStyle: 'square',
			columns: 3,
			className: { base: 'md:grid-cols-3 w-3/4' },
		},
		{
			optionStyle: 'square',
			columns: 4,
			className: { base: 'md:grid-cols-4 w-full' },
		},
	],
});

export type LinkCtaAction = { type: 'link'; href?: string };

export type CalendlyCtaAction = { type: 'calendly'; calendlyLink: string };

export type HubspotFormCtaAction = Omit<FormBlockProps, 'className'> & {
	type: 'hubspotForm';
	displayInModal: boolean;
};

export type MultipleChoiceAction = {
	type: 'multiple-choice';
	label?: string;
	title?: string;
	options: Array<{
		icon?: string;
		label?: string;
		action: BasicCtaAction;
	}>;
};

export type BasicCtaAction = LinkCtaAction | CalendlyCtaAction | HubspotFormCtaAction;

export type CtaAction = BasicCtaAction | MultipleChoiceAction;

type PopupProps = { open: boolean; setOpen: Dispatch<SetStateAction<boolean>> };

export function HubspotFormCtaAction(props: HubspotFormCtaAction) {
	return (
		<FormBlock
			hubspotFormId={props.hubspotFormId}
			buttonText={props.buttonText}
			successHeading={props.successHeading}
			successMessage={props.successMessage}
			successFile={props.successFile}
		/>
	);
}

export function HubspotFormCtaActionPopup(props: HubspotFormCtaAction & PopupProps) {
	return (
		<Popup layout='sidebar-slim' open={props.open} setOpen={props.setOpen}>
			<FormBlock
				hubspotFormId={props.hubspotFormId}
				buttonText={props.buttonText}
				successHeading={props.successHeading}
				successMessage={props.successMessage}
				successFile={props.successFile}
			/>
		</Popup>
	);
}

export function CalendlyCtaActionPopup(props: CalendlyCtaAction & PopupProps) {
	return (
		<Popup layout='modal' open={props.open} setOpen={props.setOpen}>
			<InlineWidget
				url={props.calendlyLink}
				// pageSettings={{
				// 	hideGdprBanner: true,
				// }}
				styles={{
					height: '700px',
				}}
			/>
		</Popup>
	);
}

export function MultipleChoiceCtaAction(
	props: MultipleChoiceAction & { styleType?: 'primary' | 'secondary'; optionStyle: 'square' | 'horizontal' },
) {
	const options = props.options;
	const { base, option, label } = multipleChoiceCtaActionStyles({
		optionStyle: props.optionStyle,
		// @ts-expect-error TypeScript is not smart enought to infer the correct type
		columns: options ? (options.length <= 4 ? options.length : options.length <= 6 ? 3 : 4) : 4,
	});

	const [open, setOpen] = useState(false);
	const [action, setAction] = useState<BasicCtaAction | null>(null);

	return (
		<>
			<div className='empty:hidden flex flex-col gap-6 mb-8'>
				{props.label && (
					<Text
						className={`text-center ${props.styleType === 'primary' ? 'text-text-white-secondary' : 'text-text-secondary'}`}
						styleType='label-md'
					>
						{props.label}
					</Text>
				)}
				{props.title && (
					<Text as='h2' className={`text-center`} styleType='heading-sm'>
						{props.title}
					</Text>
				)}
			</div>

			<div className='flex w-full justify-center'>
				<div className={base()}>
					{options.map((el, i) => {
						const Tag = el.action.type === 'link' ? Anchor : 'button';

						return (
							<Tag
								key={`${el.icon}-${i}-${el.label}-${el.action.type}`}
								className={option()}
								type='button'
								href={el.action.type === 'link' ? el.action.href : undefined}
								onClick={() => {
									if (el.action.type === 'link') {
										return;
									}

									setAction(el.action);
									setOpen(true);
								}}
							>
								{el.icon && <IconResolver icon={el.icon} />}

								{el.label && (
									<Text as='h3' className={label()} styleType='heading-4xs'>
										{el.label}
									</Text>
								)}

								<AnimatedIcon className={`ml-auto flex-none ${props.optionStyle === 'square' ? 'md:hidden' : ''}`}>
									<IconResolver icon={'arrow-up-right'} />
								</AnimatedIcon>

								{props.optionStyle === 'square' && (
									<AnimatedIcon
										className={`hidden lg:block sm:absolute sm:bottom-[1.5rem] sm:left-1/2 sm:-translate-x-1/2`}
										defaultEmpty={true}
									>
										<IconResolver icon={'arrow-up-right'} />
									</AnimatedIcon>
								)}
							</Tag>
						);
					})}
				</div>
			</div>

			{action?.type === 'hubspotForm' && <HubspotFormCtaActionPopup open={open} setOpen={setOpen} {...action} />}
			{action?.type === 'calendly' && <CalendlyCtaActionPopup open={open} setOpen={setOpen} {...action} />}
		</>
	);
}

export function MultipleChoiceCtaActionPopup(props: MultipleChoiceAction & PopupProps) {
	return (
		<Popup layout='sidebar-slim' open={props.open} setOpen={props.setOpen}>
			<MultipleChoiceCtaAction {...props} optionStyle='horizontal' />
		</Popup>
	);
}
